import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@/components/ui/icons/check-icon";
import {
  dropdownChevronClassName,
  dropdownPlaceholderClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";
import { useTriggerWidth } from "@/components/ui/use-trigger-width";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { toNative } from "libs/emoji";

export interface LabelOption {
  label: string;
  value: string;
  color?: string;
  emoji?: string;
  isCampaign?: boolean;
}

export interface MultiContentLabelSelectorProps {
  options: LabelOption[];
  selectedOptions: LabelOption[];
  onChange: (selected: LabelOption[]) => void;
  zIndexOfMenu?: number;
  isFetching?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  menuWidth?: "trigger" | number | string;
  size?: "sm" | "md";
  maxVisibleTags?: number | "auto";
  selectedTagTextMaxWidth?: string;
  selectedTagsLayout?: "wrap" | "single-line";
  autoFocusSearch?: boolean;
  clearable?: boolean;
  showTagRemoveActions?: boolean;
}

const CAMPAIGN_COLOR = "#edeaff";
const CAMPAIGN_ICON_COLOR = "#0e1680";
const INDETERMINATE_CHECKBOX_COLOR = "#4f31ff";
const INTER_FONT_FAMILY = "Inter, Helvetica, Arial, sans-serif";
const TAG_GAP = 4;

const SIZE_CONFIG = {
  sm: {
    trigger: "min-h-[40px] px-3 py-1.5 text-sm",
    selectAll: "px-[14px] py-[6px] text-sm",
    groupHeading: "px-[14px] pt-[10px] pb-[6px]",
    item: "px-[14px] py-[6px]",
    groupBodyBottomPadding: "pb-[6px]",
    countBadge: "h-5 min-w-5 px-1 text-[10px]",
    searchRow: "h-8 px-3",
    searchIcon: "text-[16px]",
  },
  md: {
    trigger: "min-h-[54px] px-3.5 py-2.5 text-sm",
    selectAll: "px-[14px] py-[10px] text-sm",
    groupHeading: "px-[14px] pt-[12px] pb-[4px]",
    item: "px-[14px] py-[10px]",
    groupBodyBottomPadding: "pb-[6px]",
    countBadge: "h-5 min-w-5 px-1 text-[10px]",
    searchRow: "h-10 px-[14px]",
    searchIcon: "text-[16px]",
  },
} as const;

export function MultiContentLabelSelector({
  options,
  selectedOptions,
  onChange,
  zIndexOfMenu = 50,
  isFetching = false,
  placeholder = "Select labels",
  className,
  disabled = false,
  menuWidth = "trigger",
  size = "md",
  maxVisibleTags,
  selectedTagTextMaxWidth = "160px",
  selectedTagsLayout = "wrap",
  autoFocusSearch = false,
  clearable = false,
  showTagRemoveActions = true,
}: MultiContentLabelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const isOpen = !disabled && open;
  const popupId = useId();
  const { triggerRef, triggerWidthStyle } =
    useTriggerWidth<HTMLDivElement>(isOpen);
  const selectedContentRef = useRef<HTMLDivElement>(null);
  const autoFitMeasurementRef = useRef<HTMLDivElement>(null);
  const [autoVisibleTagLimit, setAutoVisibleTagLimit] = useState(
    selectedOptions.length
  );
  const sizeConfig = SIZE_CONFIG[size];
  const visibleTagLimit =
    typeof maxVisibleTags === "number" && maxVisibleTags > 0
      ? maxVisibleTags
      : maxVisibleTags === "auto"
        ? autoVisibleTagLimit
        : undefined;

  const updateAutoVisibleTagLimit = useCallback(() => {
    if (maxVisibleTags !== "auto") return;
    const availableWidth = selectedContentRef.current?.clientWidth;
    const measurement = autoFitMeasurementRef.current;
    if (!availableWidth || !measurement) return;

    const chipWidths = [
      ...measurement.querySelectorAll<HTMLElement>("[data-auto-fit-chip]"),
    ].map((chip) => chip.getBoundingClientRect().width);
    const badgeWidths = new Map(
      [
        ...measurement.querySelectorAll<HTMLElement>(
          "[data-auto-fit-overflow]"
        ),
      ].map((badge) => [
        Number(badge.dataset.autoFitOverflow),
        badge.getBoundingClientRect().width,
      ])
    );

    setAutoVisibleTagLimit(
      calculateAutoVisibleTagCount({
        availableWidth,
        chipWidths,
        badgeWidths,
      })
    );
  }, [maxVisibleTags]);

  useLayoutEffect(() => {
    if (maxVisibleTags !== "auto") return;

    updateAutoVisibleTagLimit();
    const trigger = triggerRef.current;
    const observer =
      trigger && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateAutoVisibleTagLimit)
        : undefined;
    if (trigger) observer?.observe(trigger);
    window.addEventListener("resize", updateAutoVisibleTagLimit);

    let active = true;
    void document.fonts?.ready.then(() => {
      if (active) updateAutoVisibleTagLimit();
    });

    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("resize", updateAutoVisibleTagLimit);
    };
  }, [
    maxVisibleTags,
    selectedOptions,
    selectedTagTextMaxWidth,
    showTagRemoveActions,
    triggerRef,
    updateAutoVisibleTagLimit,
  ]);

  const selectedIds = useMemo(
    () => new Set(selectedOptions.map((opt) => opt.value)),
    [selectedOptions]
  );

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    const search = searchValue.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(search));
  }, [options, searchValue]);

  const { contentPillars, campaigns } = useMemo(() => {
    const pillars: LabelOption[] = [];
    const campaignLabels: LabelOption[] = [];

    for (const opt of filteredOptions) {
      if (opt.isCampaign) {
        campaignLabels.push(opt);
      } else {
        pillars.push(opt);
      }
    }

    return { contentPillars: pillars, campaigns: campaignLabels };
  }, [filteredOptions]);

  const displayedSelectedOptions = useMemo(
    () =>
      visibleTagLimit === undefined
        ? selectedOptions
        : selectedOptions.slice(0, visibleTagLimit),
    [selectedOptions, visibleTagLimit]
  );
  const hiddenSelectedCount =
    selectedOptions.length - displayedSelectedOptions.length;

  const filteredIds = useMemo(
    () => new Set(filteredOptions.map((option) => option.value)),
    [filteredOptions]
  );
  const selectedVisibleCount = useMemo(
    () =>
      filteredOptions.filter((option) => selectedIds.has(option.value)).length,
    [filteredOptions, selectedIds]
  );
  const isAllVisibleSelected =
    filteredOptions.length > 0 &&
    selectedVisibleCount === filteredOptions.length;
  const isSomeVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < filteredOptions.length;

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled && nextOpen) {
      return;
    }

    setOpen(nextOpen);
    setSearchValue("");
  };

  const reorderSelection = (nextSelectedIds: Set<string>) =>
    options.filter((option) => nextSelectedIds.has(option.value));

  const handleSelect = (option: LabelOption) => {
    const nextSelectedIds = new Set(selectedIds);

    if (nextSelectedIds.has(option.value)) {
      nextSelectedIds.delete(option.value);
    } else {
      nextSelectedIds.add(option.value);
    }

    onChange(reorderSelection(nextSelectedIds));
  };

  const handleRemove = (optionId: string) => {
    const nextSelectedIds = new Set(selectedIds);
    nextSelectedIds.delete(optionId);
    onChange(reorderSelection(nextSelectedIds));
  };

  const handleSelectAll = () => {
    const nextSelectedIds = new Set(selectedIds);

    if (isAllVisibleSelected) {
      for (const id of filteredIds) {
        nextSelectedIds.delete(id);
      }
    } else {
      for (const option of filteredOptions) {
        nextSelectedIds.add(option.value);
      }
    }

    onChange(reorderSelection(nextSelectedIds));
  };

  const resolvedMenuWidthStyle = resolveMenuWidthStyle({
    menuWidth,
    triggerWidthStyle,
  });

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <div
          ref={triggerRef}
          role="button"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-controls={popupId}
          aria-label={placeholder}
          aria-disabled={disabled}
          data-selected-tags-layout={selectedTagsLayout}
          data-auto-fit-labels={maxVisibleTags === "auto" || undefined}
          tabIndex={disabled ? -1 : 0}
          onPointerDown={(event) => {
            if (disabled) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onClick={(event) => {
            if (disabled) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onKeyDown={(event) => {
            if (disabled) return;

            if (event.key === "ArrowDown") {
              event.preventDefault();
              handleOpenChange(true);
              return;
            }

            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleOpenChange(!isOpen);
            }
          }}
          className={cn(
            "relative flex w-full items-center justify-between gap-2 rounded-lg border text-left transition-colors font-inter",
            "focus:outline-none",
            dropdownTriggerClassName,
            sizeConfig.trigger,
            disabled && "cursor-not-allowed",
            className
          )}
          style={{ fontFamily: INTER_FONT_FAMILY }}
        >
          <div
            ref={selectedContentRef}
            className="flex min-w-0 flex-1 items-center gap-1"
          >
            <div
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1",
                selectedTagsLayout === "single-line"
                  ? "flex-nowrap overflow-hidden"
                  : "flex-wrap"
              )}
            >
              {selectedOptions.length === 0 ? (
                <span
                  className={cn(
                    "truncate font-inter",
                    dropdownPlaceholderClassName
                  )}
                  style={{ fontFamily: INTER_FONT_FAMILY }}
                >
                  {placeholder}
                </span>
              ) : (
                displayedSelectedOptions.map((option) => (
                  <LabelChip
                    key={option.value}
                    option={option}
                    onRemove={() => {
                      if (!disabled) {
                        handleRemove(option.value);
                      }
                    }}
                    showRemove={!disabled && showTagRemoveActions}
                    textMaxWidth={selectedTagTextMaxWidth}
                    allowShrink={selectedTagsLayout === "single-line"}
                  />
                ))
              )}
            </div>
            {hiddenSelectedCount > 0 && (
              <OverflowCountBadge
                count={hiddenSelectedCount}
                className={sizeConfig.countBadge}
              />
            )}
          </div>
          {clearable && selectedOptions.length > 0 ? (
            <button
              type="button"
              aria-label="Clear labels"
              disabled={disabled}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange([]);
              }}
              className="inline-flex size-5 shrink-0 items-center justify-center rounded text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800 disabled:pointer-events-none disabled:opacity-40"
            >
              <i
                aria-hidden
                className="fa-regular fa-xmark size-5 text-center text-[16px] leading-5"
              />
            </button>
          ) : null}
          <i
            className={cn(
              "fa-regular fa-chevron-down shrink-0 text-xs transition-transform",
              dropdownChevronClassName,
              isOpen && "rotate-180"
            )}
          />
        </div>
      </PopoverTrigger>
      {maxVisibleTags === "auto" && selectedOptions.length > 0 ? (
        <div
          ref={autoFitMeasurementRef}
          aria-hidden
          className="pointer-events-none invisible fixed left-0 top-0 -z-10 flex w-max items-center gap-1 whitespace-nowrap"
        >
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              data-auto-fit-chip=""
              className="inline-flex w-max shrink-0"
            >
              <LabelChip
                option={option}
                showRemove={!disabled && showTagRemoveActions}
                textMaxWidth={selectedTagTextMaxWidth}
              />
            </span>
          ))}
          {selectedOptions.map((_, index) => {
            const count = index + 1;
            return (
              <OverflowCountBadge
                key={count}
                count={count}
                data-auto-fit-overflow={count}
                className={sizeConfig.countBadge}
              />
            );
          })}
        </div>
      ) : null}
      <PopoverContent
        id={popupId}
        role="dialog"
        aria-label={placeholder}
        className="max-h-[var(--radix-popover-content-available-height)] overflow-hidden rounded-lg border-neutral-100 p-0 shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)]"
        disableAnimation
        widthMode={menuWidth === "trigger" ? "trigger" : "custom"}
        align="start"
        style={{
          ...resolvedMenuWidthStyle,
          zIndex: zIndexOfMenu,
        }}
      >
        <div className="flex max-h-[var(--radix-popover-content-available-height)] min-h-0 flex-col overflow-hidden rounded-lg bg-white">
          <SearchRow
            size={size}
            value={searchValue}
            onChange={setSearchValue}
            autoFocus={autoFocusSearch}
          />
          <div
            data-testid="content-label-options-scroll"
            className="min-h-0 flex-1 overflow-y-auto"
          >
            {isFetching ? (
              <div className="flex items-center justify-center py-6">
                <i className="fa-regular fa-spinner-third fa-spin text-lg text-neutral-400" />
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-neutral-500">
                No labels found.
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  role="checkbox"
                  aria-checked={
                    isAllVisibleSelected
                      ? true
                      : isSomeVisibleSelected
                        ? "mixed"
                        : false
                  }
                  className={cn(
                    "flex w-full items-center gap-2 bg-white text-left font-inter font-medium text-neutral-900",
                    sizeConfig.selectAll
                  )}
                >
                  <CheckboxIndicator
                    checked={isAllVisibleSelected}
                    indeterminate={isSomeVisibleSelected}
                  />
                  <span style={{ fontFamily: INTER_FONT_FAMILY }}>
                    Select all
                  </span>
                </button>

                {contentPillars.length > 0 && (
                  <>
                    <div className="h-px w-full bg-neutral-100" />
                    <div className={sizeConfig.groupBodyBottomPadding}>
                      <div
                        className={cn(
                          "text-xs font-inter font-medium leading-4 text-neutral-500",
                          sizeConfig.groupHeading
                        )}
                        style={{ fontFamily: INTER_FONT_FAMILY }}
                      >
                        Content pillars
                      </div>
                      {contentPillars.map((option) => (
                        <LabelRow
                          key={option.value}
                          option={option}
                          isSelected={selectedIds.has(option.value)}
                          onSelect={() => handleSelect(option)}
                          className={sizeConfig.item}
                        />
                      ))}
                    </div>
                  </>
                )}

                {campaigns.length > 0 && (
                  <>
                    <div className="h-px w-full bg-neutral-100" />
                    <div className={sizeConfig.groupBodyBottomPadding}>
                      <div
                        className={cn(
                          "text-xs font-inter font-medium leading-4 text-neutral-500",
                          sizeConfig.groupHeading
                        )}
                        style={{ fontFamily: INTER_FONT_FAMILY }}
                      >
                        Campaign labels
                      </div>
                      {campaigns.map((option) => (
                        <LabelRow
                          key={option.value}
                          option={option}
                          isSelected={selectedIds.has(option.value)}
                          onSelect={() => handleSelect(option)}
                          className={sizeConfig.item}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function calculateAutoVisibleTagCount({
  availableWidth,
  chipWidths,
  badgeWidths,
  gap = TAG_GAP,
}: {
  availableWidth: number;
  chipWidths: number[];
  badgeWidths: ReadonlyMap<number, number>;
  gap?: number;
}) {
  const totalChipWidth = chipWidths.reduce((total, width) => total + width, 0);
  const allTagsWidth =
    totalChipWidth + Math.max(0, chipWidths.length - 1) * gap;
  if (allTagsWidth <= availableWidth) return chipWidths.length;

  let visibleChipWidth = totalChipWidth;
  for (
    let visibleCount = chipWidths.length - 1;
    visibleCount >= 0;
    visibleCount--
  ) {
    visibleChipWidth -= chipWidths[visibleCount] || 0;
    const hiddenCount = chipWidths.length - visibleCount;
    const visibleGaps = Math.max(0, visibleCount - 1) * gap;
    const overflowGap = visibleCount > 0 ? gap : 0;
    const overflowWidth = badgeWidths.get(hiddenCount) || 0;
    if (
      visibleChipWidth + visibleGaps + overflowGap + overflowWidth <=
      availableWidth
    ) {
      return visibleCount;
    }
  }

  return 0;
}

function OverflowCountBadge({
  count,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { count: number }) {
  return (
    <div
      {...props}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-neutral-900 font-medium text-white",
        className
      )}
    >
      +{count}
    </div>
  );
}

function resolveMenuWidthStyle({
  menuWidth,
  triggerWidthStyle,
}: {
  menuWidth: MultiContentLabelSelectorProps["menuWidth"];
  triggerWidthStyle: React.CSSProperties | undefined;
}) {
  if (menuWidth === "trigger") {
    return triggerWidthStyle;
  }

  if (typeof menuWidth === "number") {
    return { width: `${menuWidth}px`, minWidth: `${menuWidth}px` };
  }

  if (typeof menuWidth === "string") {
    return { width: menuWidth, minWidth: menuWidth };
  }

  return triggerWidthStyle;
}

function CheckboxIndicator({
  checked,
  indeterminate = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
        checked
          ? "border-blue-500 bg-blue-500 text-white"
          : indeterminate
            ? "border-blue-500 bg-[#edeaff]"
            : "border-neutral-300 bg-white"
      )}
      style={
        indeterminate ? { color: INDETERMINATE_CHECKBOX_COLOR } : undefined
      }
      data-state={
        checked ? "checked" : indeterminate ? "indeterminate" : "unchecked"
      }
    >
      {checked ? (
        <CheckIcon className="size-3 text-white" />
      ) : indeterminate ? (
        <div
          className="h-0.5 w-2.5 rounded"
          style={{ backgroundColor: INDETERMINATE_CHECKBOX_COLOR }}
        />
      ) : null}
    </div>
  );
}

function SearchRow({
  size,
  value,
  onChange,
  autoFocus,
}: {
  size: "sm" | "md";
  value: string;
  onChange: (value: string) => void;
  autoFocus: boolean;
}) {
  const sizeConfig = SIZE_CONFIG[size];

  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-2 border-b border-neutral-100 bg-white",
        sizeConfig.searchRow
      )}
    >
      <i
        className={cn(
          "fa-regular fa-magnifying-glass shrink-0 text-neutral-500",
          sizeConfig.searchIcon
        )}
        aria-hidden="true"
      />
      <input
        autoFocus={autoFocus}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search"
        className="h-full w-full bg-transparent text-sm font-inter font-normal leading-5 text-neutral-900 outline-none placeholder:text-neutral-500"
        style={{ fontFamily: INTER_FONT_FAMILY }}
      />
    </div>
  );
}

function LabelRow({
  option,
  isSelected,
  onSelect,
  className,
}: {
  option: LabelOption;
  isSelected: boolean;
  onSelect: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 bg-white text-left font-inter",
        className
      )}
    >
      <CheckboxIndicator checked={isSelected} />
      <LabelChip option={option} textMaxWidth="160px" />
    </button>
  );
}

function LabelChip({
  option,
  onRemove,
  showRemove = false,
  textMaxWidth = "160px",
  allowShrink = false,
}: {
  option: LabelOption;
  onRemove?: () => void;
  showRemove?: boolean;
  textMaxWidth?: string;
  allowShrink?: boolean;
}) {
  // A label with no emoji shows its colour swatch and nothing else — it used to
  // fall back to a hard-coded 🎉, a placeholder indistinguishable from a party
  // popper someone actually chose (SKE-4372).
  const emoji = option.isCampaign ? null : option.emoji;

  return (
    <div
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-[4px] bg-neutral-25 pr-1",
        allowShrink ? "min-w-0 shrink" : "shrink-0"
      )}
    >
      <div
        className="flex size-5 shrink-0 items-center justify-center rounded-l-[4px]"
        style={{
          backgroundColor: option.isCampaign ? CAMPAIGN_COLOR : option.color,
        }}
      >
        {option.isCampaign ? (
          <i
            className="fa-solid fa-asterisk text-[10px]"
            style={{ color: CAMPAIGN_ICON_COLOR }}
            aria-hidden="true"
          />
        ) : emoji ? (
          <span className="text-xs" aria-hidden="true">
            {toNative(emoji)}
          </span>
        ) : null}
      </div>
      <span
        title={option.label}
        className="min-w-0 truncate text-xs font-inter font-medium leading-4 text-neutral-900"
        style={{ maxWidth: textMaxWidth, fontFamily: INTER_FONT_FAMILY }}
      >
        {option.label}
      </span>
      {showRemove && onRemove && (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove();
          }}
          className="flex size-[15px] shrink-0 items-center justify-center text-neutral-500 hover:text-neutral-700"
          aria-label={`Remove ${option.label} label`}
        >
          <i className="fa-regular fa-xmark text-[12px]" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default MultiContentLabelSelector;
